import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { useParams } from "react-router-dom";

export default function PatientPage() {
  const { token } = useParams();
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const snapshot = await getDoc(doc(db, "patients", token));
        if (snapshot.exists()) {
          setPatient(snapshot.data());
        } else {
          setError("Patient not found.");
        }
      } catch (err) {
        setError("Error fetching patient data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();

    const unsub = onSnapshot(
      query(
        collection(db, "patients", token, "consultations"),
        orderBy("timestamp", "desc")
      ),
      (snapshot) => {
        setConsultations(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      }
    );

    return () => unsub();
  }, [token]);

  const addConsultation = async () => {
    if (!newNote.trim()) return;
    await addDoc(collection(db, "patients", token, "consultations"), {
      note: newNote,
      doctorName: "Dr. Example", // Replace with real auth user
      timestamp: Timestamp.now(),
    });
    setNewNote("");
  };

  if (loading) return <p>Loading patient data...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="patient-page">
      <h2>Recommended Summary Plan for Emergency Care and Treatment</h2>

      <section>
        <h3>1. This plan belongs to:</h3>
        <p>Name: {patient.fullName}</p>
        <p>Date of Birth: {patient.dob}</p>
        <p>Address: {patient.address}</p>
        <p>NHS/CHI/Health and Care Number: {patient.healthNumber}</p>
        <p>Preferred Name: {patient.preferredName}</p>
        <p>Date Completed: {patient.dateCompleted}</p>
      </section>

      <section>
        <h3>2. Shared understanding of my health and current condition</h3>
        <p>{patient.summary}</p>
        <p>Other care planning documents: {patient.otherDocs}</p>
        <p>Legal welfare proxy: {patient.legalProxy}</p>
      </section>

      <section>
        <h3>3. What matters to me most about my treatment and care in an emergency</h3>
        <p>What I most value: {patient.whatValue}</p>
        <p>What I most fear / wish to avoid: {patient.whatFear}</p>
      </section>

      <section>
        <h3>5. Capacity for involvement in making this plan</h3>
        <p>Has capacity? {patient.capacity}</p>
        {patient.capacity === "No" && (
          <p>Reason: {patient.lackCapacityReason}</p>
        )}
      </section>

      <section>
        <h3>6. Involvement in making this plan</h3>
        <p>Involvement: {patient.involvement}</p>
        {patient.involvement === "D" && (
          <p>Reason: {patient.involvementOther}</p>
        )}
      </section>

      <section>
        <h3>7. Clinicians' Details</h3>
        <table>
          <thead>
            <tr>
              <th>Grade/Speciality</th>
              <th>Name</th>
              <th>GMC/NMC/HCPC No.</th>
              <th>Date & Time</th>
            </tr>
          </thead>
          <tbody>
            {patient.clinicians.map((c, i) => (
              <tr key={i}>
                <td>{c.grade}</td>
                <td>{c.name}</td>
                <td>{c.number}</td>
                <td>{c.dateTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3>8. Emergency Contacts</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role & Relationship</th>
              <th>Contact Number</th>
              <th>Signature</th>
            </tr>
          </thead>
          <tbody>
            {patient.emergencyContacts.map((c, i) => (
              <tr key={i}>
                <td>{c.name}</td>
                <td>{c.role}</td>
                <td>{c.contactNo}</td>
                <td>{c.signature}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3>9. Form Reviewed</h3>
        <p>(To be updated on review.)</p>
      </section>

      <section>
        <h3>Consultations</h3>
        <ul>
          {consultations.map((c) => (
            <li key={c.id}>
              <strong>{c.doctorName}</strong> —{" "}
              {new Date(c.timestamp.seconds * 1000).toLocaleString()} <br />
              {c.note}
            </li>
          ))}
        </ul>

        <textarea
          placeholder="Add new consultation note"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
        />
        <button onClick={addConsultation}>Add Consultation</button>
      </section>
    </div>
  );
}